

import { useState, useEffect } from "react"
import { Modal, Button, Form, Alert, Spinner, Row, Col } from "react-bootstrap"
import axios from "axios"
import { useHistory } from "react-router-dom"

const QuoteRequestModal = ({
  show,
  onHide,
  productId = null,
  serviceId = null,
  sellerId,
  itemName,
  itemImage,
  itemPrice,
  existingQuote = null, // For editing existing quotes
  isEditing = false,
  onSuccess,
}) => {
  const history = useHistory()
  const [formData, setFormData] = useState({
    message: existingQuote?.requestDetails?.message || "",
    suggestedPrice: existingQuote?.requestDetails?.suggestedPrice || "",
    attachments: [],
    editReason: "", // For quote edits
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState(false)

  useEffect(() => {
    if (!show) return

    if (isEditing && existingQuote) {
      setFormData({
        message: existingQuote.requestDetails?.message || "",
        suggestedPrice: existingQuote.requestDetails?.suggestedPrice ?? "",
        attachments: [],
        editReason: "",
      })
    } else if (!isEditing) {
      setFormData({
        message: "",
        suggestedPrice: "",
        attachments: [],
        editReason: "",
      })
    }

    setError("")
    setSuccess(false)
    setLoading(false)
  }, [show, isEditing, existingQuote])

  const authInfo = JSON.parse(localStorage.getItem("authInfo"))
  const buyerId = authInfo?.id

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files)
    setFormData((prev) => ({
      ...prev,
      attachments: files,
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!authInfo) {
      setError("Please login to request a quote")
      return
    }

    if (!formData.message.trim()) {
      setError("Please enter a message for your quote request")
      return
    }

    if (isEditing && !formData.editReason.trim()) {
      setError("Please provide a reason for editing this quote")
      return
    }

    setLoading(true)
    setError("")

    try {
      let response

      if (isEditing) {
        // Edit existing quote
        response = await axios.put(
          `user/quote/${existingQuote._id}/edit`,
          {
            buyerId: buyerId,
            reason: formData.editReason,
            newPrice: formData.suggestedPrice ? Number.parseFloat(formData.suggestedPrice) : null,
            message: formData.message,
          },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${authInfo.token}`,
            },
          },
        )
      } else {
        // Create new quote
        const formDataToSend = new FormData()
        formDataToSend.append("buyerId", buyerId)
        formDataToSend.append("sellerId", sellerId)
        formDataToSend.append("message", formData.message)

        if (formData.suggestedPrice) {
          formDataToSend.append("suggestedPrice", formData.suggestedPrice)
        }

        if (productId) {
          formDataToSend.append("productId", productId)
        }
        if (serviceId) {
          formDataToSend.append("serviceId", serviceId)
        }

        // Append attachments
        formData.attachments.forEach((file) => {
          formDataToSend.append("attachments", file)
        })

        response = await axios.post("/user/quote/request", formDataToSend, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${authInfo.token}`,
          },
        })
      }

      if (response.data.status) {
        const newQuoteId = response.data.data?._id;
        console.log("NEW QUOTE ID: ", newQuoteId)
        console.log(response)
        setSuccess(true)
        setFormData({ message: "", suggestedPrice: "", attachments: [], editReason: "" })
        setTimeout(() => {
          setSuccess(false)
          onHide()
          if (onSuccess) {
            onSuccess(newQuoteId)
          } else if (newQuoteId) {
            history.push({
              pathname: "/my-quotes",
              search: `?quoteId=${newQuoteId}`,
            })
          }
        }, 2000)
      } else {
        setError(response.data.message || `Failed to ${isEditing ? "update" : "submit"} quote request`)
      }
    } catch (error) {
      console.error("Quote request error:", error)
      setError(error.response?.data?.message || `Failed to ${isEditing ? "update" : "submit"} quote request`)
    } finally {
      setLoading(false)
    }
  }

  const resetModal = () => {
    setFormData({ message: "", suggestedPrice: "", attachments: [], editReason: "" })
    setError("")
    setSuccess(false)
    setLoading(false)
  }

  const canEdit =
    existingQuote && !existingQuote.isFinal && existingQuote.status === "pending" && existingQuote.priceEditableByBuyer

  return (
    <Modal
      show={show}
      onHide={() => {
        onHide()
        resetModal()
      }}
      size="lg"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>{isEditing ? "Edit Quote Request" : "Request Custom Quote"}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Item Preview */}
        <div className="d-flex align-items-center mb-4 p-3 bg-light rounded">
          <img
            src={itemImage || "/placeholder.svg"}
            alt={itemName}
            style={{ width: "80px", height: "80px", objectFit: "cover" }}
            className="rounded me-3"
          />
          <div>
            <h6 className="mb-1">{itemName}</h6>
            <p className="mb-0 text-muted">
              {productId ? "Product" : "Service"} • ${itemPrice}
            </p>
            {existingQuote && (
              <div className="mt-2">
                <small className="text-info">
                  Quote ID: {existingQuote.quoteCode} • Status: {existingQuote.status}
                </small>
              </div>
            )}
          </div>
        </div>

        {/* Edit Warning */}
        {isEditing && !canEdit && (
          <Alert variant="warning" className="mb-3">
            <i className="fas fa-exclamation-triangle me-2"></i>
            This quote cannot be edited because it has been finalized or already responded to by the seller.
          </Alert>
        )}

        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}

        {success && (
          <Alert variant="success" className="mb-3">
            <div className="d-flex align-items-center">
              <i className="fas fa-check-circle me-2"></i>
              Quote request {isEditing ? "updated" : "submitted"} successfully!
              {!isEditing && " The seller will respond soon."}
            </div>
          </Alert>
        )}

        {!success && (canEdit || !isEditing) && (
          <Form onSubmit={handleSubmit}>
            {isEditing && (
              <Form.Group className="mb-3">
                <Form.Label>
                  Reason for Edit <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="editReason"
                  value={formData.editReason}
                  onChange={handleInputChange}
                  placeholder="Why are you editing this quote request?"
                  required
                />
                <Form.Text className="text-muted">Please explain why you're making changes to this quote</Form.Text>
              </Form.Group>
            )}

            <Row>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    Describe your requirements <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Please describe your specific requirements, customizations, or questions about this item..."
                    required
                  />
                  <Form.Text className="text-muted">Be as specific as possible to get an accurate quote</Form.Text>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Suggested Price (Optional)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0"
                    name="suggestedPrice"
                    value={formData.suggestedPrice}
                    onChange={handleInputChange}
                    placeholder="Your price suggestion"
                  />
                  <Form.Text className="text-muted">Optional: Suggest your preferred price</Form.Text>
                </Form.Group>
              </Col>
            </Row>

            {!isEditing && (
              <Form.Group className="mb-4">
                <Form.Label>Attachments (Optional)</Form.Label>
                <Form.Control
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.txt,.zip,.rar,.xlsx,.xls,.ppt,.pptx"
                />
                <Form.Text className="text-muted">
                  You can attach up to 5 files (images, documents, archives). Max 10MB per file.
                </Form.Text>
                {formData.attachments.length > 0 && (
                  <div className="mt-2">
                    <small className="text-success">{formData.attachments.length} file(s) selected</small>
                  </div>
                )}
              </Form.Group>
            )}

            {/* Show existing edit history if editing */}
            {isEditing && existingQuote?.buyerEdits && existingQuote.buyerEdits.length > 0 && (
              <div className="mb-4">
                <h6>Previous Edits:</h6>
                <div className="bg-light p-3 rounded" style={{ maxHeight: "150px", overflowY: "auto" }}>
                  {existingQuote.buyerEdits.map((edit, index) => (
                    <div key={index} className="mb-2 pb-2 border-bottom">
                      <small className="text-muted">
                        {new Date(edit.editedAt).toLocaleDateString()} - {edit.reason}
                      </small>
                      {edit.previousPrice && edit.newPrice && (
                        <div>
                          <small>
                            Price changed from ${edit.previousPrice} to ${edit.newPrice}
                          </small>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="d-flex justify-content-end gap-2">
              <Button
                variant="outline-secondary"
                onClick={() => {
                  onHide()
                  resetModal()
                }}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={loading} className="btn_yellow_2">
                {loading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    {isEditing ? "Updating..." : "Submitting..."}
                  </>
                ) : isEditing ? (
                  "Update Quote Request"
                ) : (
                  "Submit Quote Request"
                )}
              </Button>
            </div>
          </Form>
        )}
      </Modal.Body>
    </Modal>
  )
}

export default QuoteRequestModal